import { useEffect, useRef } from 'react';

export interface DraftPersistenceOptions<TValues> {
  /** Callback to persist current values (app owns the storage logic) */
  save: (values: TValues) => void;
  /** Callback to load previously saved values, or null if none */
  load: () => Partial<TValues> | null;
  /** Skip persistence until ready (e.g., catalog not loaded yet). Default: true */
  enabled?: boolean;
  /** Debounce interval for auto-save in ms. Default: 400 */
  debounceMs?: number;
}

/**
 * Hook that auto-saves form values to persistent storage (debounced)
 * and restores them on mount.
 *
 * Designed to be form-library-agnostic: receives getValues/setValues
 * callbacks that work with any state management approach.
 *
 * @example
 * ```ts
 * useDraftPersistence(
 *   () => form.state.values,
 *   (draft) => {
 *     for (const [k, v] of Object.entries(draft)) {
 *       form.setFieldValue(k, v);
 *     }
 *   },
 *   {
 *     save: saveEducationDraft,
 *     load: () => loadOnboardingDraft()?.education ?? null,
 *     enabled: !catalogLoading,
 *   },
 * );
 * ```
 */
export function useDraftPersistence<TValues extends Record<string, unknown>>(
  getValues: () => TValues,
  setValues: (values: Partial<TValues>) => void,
  options: DraftPersistenceOptions<TValues>
): void {
  const { save, load, enabled = true, debounceMs = 400 } = options;
  const hydrated = useRef(false);
  const setValuesRef = useRef(setValues);
  setValuesRef.current = setValues;
  const loadRef = useRef(load);
  loadRef.current = load;

  // Hydrate once on mount
  useEffect(() => {
    if (hydrated.current) return;
    const draft = loadRef.current();
    if (draft) {
      setValuesRef.current(draft);
    }
    hydrated.current = true;
  }, []);

  // Auto-save on change (debounced)
  const values = getValues();
  useEffect(() => {
    if (!enabled) return;
    const t = window.setTimeout(() => save(values), debounceMs);
    return () => window.clearTimeout(t);
  }, [values, enabled, debounceMs, save]);
}
